import { Slot, Slottable } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { LoaderCircleIcon } from 'lucide-react'
import { type ButtonHTMLAttributes, type PropsWithChildren, forwardRef } from 'react'

import { cn } from '@/lib/utils'

import { buttonVariants } from './variant'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ className, variant = 'default', size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    const usesSecondaryAsDisabled = variant === 'black-secondary' && disabled && !loading

    return (
      <Comp
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          buttonVariants({
            variant: usesSecondaryAsDisabled ? 'secondary' : variant,
            size,
            className,
          }),
          loading && 'relative text-transparent'
        )}
        {...props}
      >
        {loading && (
          <span
            className={cn('absolute left-1/2 top-1/2', 'aspect-square -translate-x-1/2 -translate-y-1/2', {
              'text-muted':
                variant === 'default' ||
                variant === 'black' ||
                variant === 'black-secondary' ||
                variant === 'destructive',
              'text-slate-800':
                variant === 'secondary' || variant === 'ghost' || variant === 'link' || variant === 'outline',
            })}
          >
            <LoaderCircleIcon className="animate-spin" />
          </span>
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    )
  }
)
Button.displayName = 'Button'
